import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import InstallPWA from '@/components/InstallPWA';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import ChatbotWidget from '@/components/ChatbotWidget';
import InstallAppBanner from '@/components/InstallAppBanner';
import RouteShimmer from '@/components/RouteShimmer';

// ── Eager (first-impression critical paths — keep instant) ─────────────────
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import DiscoverPage from '@/pages/DiscoverPage';
import RegisterPage from '@/pages/RegisterPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { AdminLoginPage, TelecallerLoginPage, MemberLoginPage } from '@/pages/RoleLoginPage';

// ── Lazy: other public pages (most visitors don't need them at first paint) ─
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const MarketingLanding = lazy(() => import('@/pages/MarketingLanding'));
const VerifyMemberPage = lazy(() => import('@/pages/VerifyMemberPage'));
const PublicOffersPage = lazy(() => import('@/pages/PublicOffersPage'));
const GalleryPage = lazy(() => import('@/pages/GalleryPage'));
const MemberVerifyPage = lazy(() => import('@/pages/MemberVerifyPage'));
const PublicAboutPage = lazy(() => import('@/pages/AboutUsPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const BrochurePage = lazy(() => import('@/pages/BrochurePage'));
const MembershipPage = lazy(() => import('@/pages/MembershipPage'));
const PublicNewsPage = lazy(() => import('@/pages/BitzNewsPage'));
const NewsDetailPage = lazy(() => import('@/pages/NewsDetailPage'));
const PartnerApplyPage = lazy(() => import('@/pages/PartnerApplyPage'));
const VendorApplyPage = lazy(() => import('@/pages/VendorApplyPage'));
const VendorDashboard = lazy(() => import('@/pages/VendorDashboard'));
const PartnerLoginPage = lazy(() => import('@/pages/PartnerLoginPage'));
const PartnerPortalPage = lazy(() => import('@/pages/PartnerPortalPage'));
const PartnerMapPage = lazy(() => import('@/pages/PartnerMapPage'));

// ── Lazy: Daily Needs (separate registration vertical) ─────────────────────
const DailyNeedsRegisterPage = lazy(() => import('@/pages/DailyNeedsRegisterPage'));
const DailyNeedsLoginPage = lazy(() => import('@/pages/DailyNeedsLoginPage'));
const DailyNeedsBrowsePage = lazy(() => import('@/pages/DailyNeedsBrowsePage'));
const DailyNeedsCardPage = lazy(() => import('@/pages/DailyNeedsCardPage'));

// ── Lazy: DSA portal ───────────────────────────────────────────────────────
const DSAPortalPage = lazy(() => import('@/pages/DSAPortalPage'));

// ── DEV verification (BITZ-016) — TEMP, remove after sign-off ────────────
const CardNameTest = lazy(() => import('@/pages/dev/CardNameTest'));

// ── Lazy: Member dashboard area (only loaded after login) ──────────────────
const MemberDashboard = lazy(() => import('@/pages/member/MemberDashboard'));
const MaintenancePaymentPage = lazy(() => import('@/pages/member/MaintenancePaymentPage'));
const MemberDailyNeedsPage = lazy(() => import('@/pages/member/MemberDailyNeedsPage'));

// ── Lazy: Admin area (47 pages — aggressively split, normal visitors never hit) ─
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const CEODashboard = lazy(() => import('@/pages/admin/CEODashboard'));
const MembersPage = lazy(() => import('@/pages/admin/MembersPage'));
const LeadsPage = lazy(() => import('@/pages/admin/LeadsPage'));
const PlansPage = lazy(() => import('@/pages/admin/PlansPage'));
const PartnersPage = lazy(() => import('@/pages/admin/PartnersPage'));
const TelecallersPage = lazy(() => import('@/pages/admin/TelecallersPage'));
const TelecallerAssignmentPage = lazy(() => import('@/pages/admin/TelecallerAssignmentPage'));
const CCEReportPage = lazy(() => import('@/pages/admin/CCEReportPage'));
const OfferRequestsPage = lazy(() => import('@/pages/admin/OfferRequestsPage'));
const ReportsPage = lazy(() => import('@/pages/admin/ReportsPage'));
const CEOReportsPage = lazy(() => import('@/pages/admin/CEOReportsPage'));
const MemberPrintPage = lazy(() => import('@/pages/admin/MemberPrintPage'));
const ContentPage = lazy(() => import('@/pages/admin/ContentPage'));
const PaymentsPage = lazy(() => import('@/pages/admin/PaymentsPage'));
const CouponsPage = lazy(() => import('@/pages/admin/CouponsPage'));
const MaintenancePage = lazy(() => import('@/pages/admin/MaintenancePage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const EventsPage = lazy(() => import('@/pages/admin/EventsPage'));
const OfflineMemberPage = lazy(() => import('@/pages/admin/OfflineMemberPage'));
const MissingFilesPage = lazy(() => import('@/pages/admin/MissingFilesPage'));
const ReferralsPage = lazy(() => import('@/pages/admin/ReferralsPage'));
const MediaManagementPage = lazy(() => import('@/pages/admin/MediaManagementPage'));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage'));
const AboutUsPage = lazy(() => import('@/pages/admin/AboutUsPage'));
const SubscriptionsPage = lazy(() => import('@/pages/admin/SubscriptionsPage'));
const CardTemplatesPage = lazy(() => import('@/pages/admin/CardTemplatesPage'));
const PromotionsPage = lazy(() => import('@/pages/admin/PromotionsPage'));
const ControlTowerPage = lazy(() => import('@/pages/admin/ControlTowerPage'));
const NotificationsLogPage = lazy(() => import('@/pages/admin/NotificationsLogPage'));
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'));
const BitzNewsPage = lazy(() => import('@/pages/admin/BitzNewsPage'));
const SystemHealthPage = lazy(() => import('@/pages/admin/SystemHealthPage'));
const DailyNeedsPage = lazy(() => import('@/pages/admin/DailyNeedsPage'));
const DailyNeedsPlansPage = lazy(() => import('@/pages/admin/DailyNeedsPlansPage'));
const DailyNeedsSubscriptionsPage = lazy(() => import('@/pages/admin/DailyNeedsSubscriptionsPage'));
const PartnerAdsPage = lazy(() => import('@/pages/admin/PartnerAdsPage'));
const EmployeesPage = lazy(() => import('@/pages/admin/EmployeesPage'));
const BrandKitPage = lazy(() => import('@/pages/admin/BrandKitPage'));
const CampaignsPage = lazy(() => import('@/pages/admin/CampaignsPage'));
const CommunicationCentrePage = lazy(() => import('@/pages/admin/CommunicationCentrePage'));
const FrontOfficeAdminPage = lazy(() => import('@/pages/admin/FrontOfficeAdminPage'));
const FrontOfficePortal = lazy(() => import('@/pages/frontoffice/FrontOfficePortal'));
const RestaurantPOS = lazy(() => import('@/pages/restaurant/RestaurantPOS'));
const RestaurantAdminPage = lazy(() => import('@/pages/admin/RestaurantAdminPage'));
const WeekendPromoPage = lazy(() => import('@/pages/admin/WeekendPromoPage'));
const SocialCalendarPage = lazy(() => import('@/pages/admin/SocialCalendarPage'));
const BusinessReportPage = lazy(() => import('@/pages/admin/BusinessReportPage'));
const EmailTemplatesPage = lazy(() => import('@/pages/admin/EmailTemplatesPage'));
const EmailRemindersPage = lazy(() => import('@/pages/admin/EmailRemindersPage'));
const AccountsPage = lazy(() => import('@/pages/admin/AccountsPage'));
const FeedbackAdminPage = lazy(() => import('@/pages/admin/FeedbackAdminPage'));
const ClubOpsPage = lazy(() => import('@/pages/admin/ClubOpsPage'));
const HRPage = lazy(() => import('@/pages/admin/HRPage'));
const BookingsAdminPage = lazy(() => import('@/pages/admin/BookingsAdminPage'));
const DepartmentAdminsPage = lazy(() => import('@/pages/admin/DepartmentAdminsPage'));
const PropertyAdminPage = lazy(() => import('@/pages/admin/PropertyAdminPage'));
const PropertyConnectPage = lazy(() => import('@/pages/PropertyConnectPage'));
const FeedbackPage = lazy(() => import('@/pages/FeedbackPage'));
const TelecallerFollowUps = lazy(() => import('@/components/FollowUpCalls').then((m) => ({ default: m.TelecallerFollowUps })));
const MarketingContactsPage = lazy(() => import('@/pages/admin/MarketingContactsPage'));
const PublicOfferPage = lazy(() => import('@/pages/PublicOfferPage'));
const PromoOfferPage = lazy(() => import('@/pages/PromoOfferPage'));
const DSAManagementPage = lazy(() => import('@/pages/admin/DSAManagementPage'));
const DataImportPage = lazy(() => import('@/pages/admin/DataImportPage'));
const PhotoApprovalsPage = lazy(() => import('@/pages/admin/PhotoApprovalsPage'));
const FamilyApprovalsPage = lazy(() => import('@/pages/admin/FamilyApprovalsPage'));
const CollectionReportPage = lazy(() => import('@/pages/admin/CollectionReportPage'));
const SMSLogsPage = lazy(() => import('@/pages/admin/SMSLogsPage'));
const DailyNeedsBulkUploadPage = lazy(() => import('@/pages/admin/DailyNeedsBulkUploadPage'));
const PaymentLinksPage = lazy(() => import('@/pages/admin/PaymentLinksPage'));
const StaffIdCardPage = lazy(() => import('@/pages/admin/StaffIdCardPage'));
const CashApprovalsPage = lazy(() => import('@/pages/admin/CashApprovalsPage'));

// ── Lazy: Telecaller area ──────────────────────────────────────────────────
const TelecallerPortalModule = lazy(() => import('@/pages/telecaller/TelecallerPortal'));
const TelecallerExtraModule = lazy(() => import('@/pages/telecaller/TelecallerExtraPages'));

// Re-export wrappers for the named telecaller exports via single lazy chunk.
// We use a tiny "namespace adapter" pattern so each telecaller route ref still
// works while only loading the underlying file once.
const tcLazy = (name) =>
  lazy(async () => {
    const mod = await import('@/pages/telecaller/TelecallerPortal');
    return { default: mod[name] };
  });
const tcExtraLazy = (name) =>
  lazy(async () => {
    const mod = await import('@/pages/telecaller/TelecallerExtraPages');
    return { default: mod[name] };
  });
const TelecallerLayout = tcLazy('TelecallerLayout');
const TelecallerDashboard = tcLazy('TelecallerDashboard');
const TelecallerTasks = tcLazy('TelecallerTasks');
const TelecallerCallLogs = tcLazy('TelecallerCallLogs');
const TelecallerNewSale = tcLazy('TelecallerNewSale');
const TelecallerLeads = tcLazy('TelecallerLeads');
const TelecallerProfile = tcExtraLazy('TelecallerProfile');
const TelecallerMemberInfo = tcExtraLazy('TelecallerMemberInfo');
const TelecallerAffiliates = tcExtraLazy('TelecallerAffiliates');
const TelecallerFAQ = tcExtraLazy('TelecallerFAQ');

// ── Lazy: Cash collection (telecaller-only) ────────────────────────────────
const CashCollectionPage = lazy(() => import('@/components/CashCollectionPage'));

// Touch the eagerly-loaded telecaller modules so tree-shaking doesn't drop them
// (they're aliased through `tcLazy` above; reference here to keep import-graph clean).
void TelecallerPortalModule;
void TelecallerExtraModule;

import '@/App.css';

const FloatingWidgets = () => {
  const path = useLocation().pathname;
  if (['/admin', '/telecaller', '/restaurant', '/front-office'].some((p) => path.startsWith(p))) return null;
  return (
    <>
      <WhatsAppFloat />
      <ChatbotWidget />
      <InstallAppBanner />
    </>
  );
};

// Safety net: any modal that forgets to release body.style.overflow / pointer-events
// would silently freeze the entire mobile site. On every route change we wipe these.
const BodyStyleResetter = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const b = document.body;
    if (b.style.overflow) b.style.overflow = '';
    if (b.style.pointerEvents) b.style.pointerEvents = '';
    if (b.style.position === 'fixed') b.style.position = '';
    b.removeAttribute('data-scroll-locked');
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BodyStyleResetter />
        <div className="App">
          <InstallPWA />
          <FloatingWidgets />

          <Suspense fallback={<RouteShimmer />}>
            <Routes>
              {/* ── Public Routes ──────────────────────────────────────── */}
              <Route path="/" element={<HomePage />} />
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/social" element={<LandingPage />} />
              <Route path="/join" element={<RegisterPage />} />
              <Route path="/marketing" element={<MarketingLanding />} />
              <Route path="/promo" element={<MarketingLanding />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/telecaller/login" element={<TelecallerLoginPage />} />
              <Route path="/member/login" element={<MemberLoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/member/register" element={<RegisterPage />} />
              <Route path="/offers" element={<PublicOffersPage />} />
              <Route path="/offer/:token" element={<PublicOfferPage />} />
              <Route path="/promo/:rid" element={<PromoOfferPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/about" element={<PublicAboutPage />} />
              <Route path="/about-us" element={<PublicAboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/brochure" element={<BrochurePage />} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/memberships" element={<MembershipPage />} />
              <Route path="/tiers" element={<MembershipPage />} />
              <Route path="/news" element={<PublicNewsPage />} />
              <Route path="/news/:slug" element={<NewsDetailPage />} />
              <Route path="/bitz-news" element={<PublicNewsPage />} />
              <Route path="/bitz-news/:slug" element={<NewsDetailPage />} />
              <Route path="/verify/:memberId" element={<MemberVerifyPage />} />
              <Route path="/front-office" element={<ProtectedRoute allowedRoles={['front_office', 'admin', 'super_admin']}><FrontOfficePortal /></ProtectedRoute>} />
              <Route path="/pool" element={<Navigate to="/front-office?tab=pool" replace />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/restaurant" element={<ProtectedRoute allowedRoles={['restaurant', 'front_office', 'admin', 'super_admin']}><RestaurantPOS /></ProtectedRoute>} />
              <Route path="/partner-apply" element={<PartnerApplyPage />} />
              <Route path="/vendor-apply" element={<VendorApplyPage />} />
              <Route path="/vendor-dashboard" element={<VendorDashboard />} />
              <Route path="/partner-login" element={<PartnerLoginPage />} />
              <Route path="/partner-portal" element={<PartnerPortalPage />} />
              <Route path="/partner-map" element={<PartnerMapPage />} />
              <Route path="/daily-needs" element={<DailyNeedsBrowsePage />} />
              <Route path="/property-connect" element={<PropertyConnectPage />} />
              <Route path="/daily-needs/browse" element={<DailyNeedsBrowsePage />} />

              {/* ── DEV verification (BITZ-016) — TEMP, remove after sign-off ── */}
              <Route path="/dev/card-name-test" element={<CardNameTest />} />

              {/* ── Daily Needs Separate Vertical ───────────────────────── */}
              <Route path="/daily-needs/register" element={<DailyNeedsRegisterPage />} />
              <Route path="/daily-needs/login" element={<DailyNeedsLoginPage />} />
              <Route path="/daily-needs/card" element={<DailyNeedsCardPage />} />
              <Route path="/daily-needs/dashboard" element={<DailyNeedsCardPage />} />

              {/* ── DSA & Executive Portals ─────────────────────────────── */}
              <Route path="/dsa-portal" element={<DSAPortalPage />} />

              {/* ── Member Routes ───────────────────────────────────────── */}
              <Route
                path="/member"
                element={
                  <ProtectedRoute allowedRoles={['member']}>
                    <MemberDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/member/maintenance"
                element={
                  <ProtectedRoute allowedRoles={['member']}>
                    <MaintenancePaymentPage />
                  </ProtectedRoute>
                }
              />

              {/* ── Admin Routes ────────────────────────────────────────── */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing']}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="ceo" element={<CEODashboard />} />
                <Route path="members" element={<MembersPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="plans" element={<PlansPage />} />
                <Route path="partners" element={<PartnersPage />} />
                <Route path="telecallers" element={<TelecallersPage />} />
                <Route path="telecaller-assignment" element={<TelecallerAssignmentPage />} />
                <Route path="cce-reports" element={<CCEReportPage />} />
                <Route path="offer-enquiries" element={<OfferRequestsPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="coupons" element={<CouponsPage />} />
                <Route path="maintenance" element={<MaintenancePage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="business-snapshot" element={<CEOReportsPage />} />
                <Route path="members/:memberId/print" element={<MemberPrintPage />} />
                <Route path="content" element={<ContentPage />} />
                <Route path="admin-users" element={<AdminUsersPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="members/add" element={<OfflineMemberPage />} />
                <Route path="members/missing-files" element={<MissingFilesPage />} />
                <Route path="referrals" element={<ReferralsPage />} />
                <Route path="media" element={<MediaManagementPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="about-us" element={<AboutUsPage />} />
                <Route path="card-templates" element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <CardTemplatesPage />
                  </ProtectedRoute>
                } />
                <Route path="promotions" element={<PromotionsPage />} />
                <Route path="control-tower" element={<ControlTowerPage />} />
                <Route path="notifications-log" element={<NotificationsLogPage />} />
                <Route path="audit-log" element={<AuditLogPage />} />
                <Route path="bitz-news" element={<BitzNewsPage />} />
                <Route path="system-health" element={<SystemHealthPage />} />
                <Route path="daily-needs" element={<DailyNeedsPage />} />
                <Route path="daily-needs-plans" element={<DailyNeedsPlansPage />} />
                <Route path="daily-needs-subscriptions" element={<DailyNeedsSubscriptionsPage />} />
                <Route path="partner-ads" element={<PartnerAdsPage />} />
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="dsa" element={<DSAManagementPage />} />
                <Route path="data-import" element={<DataImportPage />} />
                <Route path="cash-approvals" element={<CashApprovalsPage />} />
                <Route path="photo-approvals" element={<PhotoApprovalsPage />} />
                <Route path="family-approvals" element={<FamilyApprovalsPage />} />
                <Route path="collection-report" element={<CollectionReportPage />} />
                <Route path="sms-logs" element={<SMSLogsPage />} />
                <Route path="daily-needs-bulk-upload" element={<DailyNeedsBulkUploadPage />} />
                <Route path="payment-links" element={<PaymentLinksPage />} />
                <Route path="brand-kit" element={<BrandKitPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="communication" element={<CommunicationCentrePage />} />
                <Route path="front-office" element={<FrontOfficeAdminPage />} />
                <Route path="restaurant" element={<RestaurantAdminPage />} />
                <Route path="weekend-promo" element={<WeekendPromoPage />} />
                <Route path="social-calendar" element={<SocialCalendarPage />} />
                <Route path="business-report" element={<BusinessReportPage />} />
                <Route path="email-templates" element={<EmailTemplatesPage />} />
                <Route path="email-reminders" element={<EmailRemindersPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                <Route path="feedback" element={<FeedbackAdminPage />} />
                <Route path="property" element={<PropertyAdminPage />} />
                <Route path="club" element={<ClubOpsPage />} />
                <Route path="hr" element={<HRPage />} />
                <Route path="bookings" element={<BookingsAdminPage />} />
                <Route path="department-admins" element={<ProtectedRoute allowedRoles={['super_admin']}><DepartmentAdminsPage /></ProtectedRoute>} />
                <Route path="contacts" element={<MarketingContactsPage />} />
                <Route path="staff/:staffId/id-card" element={<StaffIdCardPage />} />
              </Route>

              {/* ── Telecaller Routes ───────────────────────────────────── */}
              <Route
                path="/telecaller"
                element={
                  <ProtectedRoute allowedRoles={['telecaller', 'admin', 'super_admin']}>
                    <TelecallerLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<TelecallerDashboard />} />
                <Route path="leads" element={<TelecallerLeads />} />
                <Route path="tasks" element={<TelecallerTasks />} />
                <Route path="members" element={<TelecallerTasks />} />
                <Route path="call-logs" element={<TelecallerCallLogs />} />
                <Route path="followups" element={<TelecallerFollowUps />} />
                <Route path="new-sale" element={<TelecallerNewSale />} />
                <Route path="cash-collection" element={<CashCollectionPage />} />
                <Route path="profile" element={<TelecallerProfile />} />
                <Route path="member-info" element={<TelecallerMemberInfo />} />
                <Route path="affiliates" element={<TelecallerAffiliates />} />
                <Route path="faq" element={<TelecallerFAQ />} />
              </Route>

              {/* Silence linter for daily-needs member dashboard route alias */}
              <Route path="/_unused_member_dn" element={<MemberDailyNeedsPage />} />

              {/* Fallback — branded 404 page (replaces silent redirect to "/" so mistyped URLs
                  don't feel blank/search-like) */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1A1A1C',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#F5F5F7',
            zIndex: 99999,
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
